
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { CompanyStage, TherapeuticArea, Region } from '../../types';
import { ArrowRight, Building2, Target, AlertCircle } from 'lucide-react';

export const ClientOnboarding: React.FC = () => {
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    companyName: '',
    stage: CompanyStage.PRECLINICAL,
    therapeuticAreas: [] as TherapeuticArea[],
    regionsOfInterest: [] as Region[],
  });

  const handleFinish = async () => {
    setError('');
    if (!user) {
        setError('Authentication lost. Please login again.');
        return;
    }

    setIsSaving(true);

    try {
      // Insert into Supabase
      const { error: dbError } = await supabase.from('clients').insert({
        owner_id: user.id,
        company_name: formData.companyName,
        development_stage: formData.stage,
        therapeutic_focus: formData.therapeuticAreas,
        target_regions: formData.regionsOfInterest
      });

      if (dbError) throw dbError;

      await completeOnboarding();
      navigate('/client-dashboard');
    } catch (err: any) {
      console.error('Client Onboarding Error:', err);
      setError(err.message || 'Failed to save profile. Please try again.');
      setIsSaving(false);
    }
  };

  const toggleSelection = <T,>(item: T, current: T[], field: string) => {
    const newSelection = current.includes(item) 
        ? current.filter(i => i !== item)
        : [...current, item];
    setFormData({ ...formData, [field]: newSelection });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
       <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-blue-900 px-8 py-6">
              <h1 className="text-2xl font-bold text-white">Welcome to RegulatorySherpas</h1>
              <p className="text-blue-200 mt-1">Help us personalize your search experience.</p>
          </div>
          
          <div className="p-8 space-y-8">
              {error && (
               <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-4 flex items-center gap-2">
                 <AlertCircle className="w-5 h-5" />
                 {error}
               </div>
              )}

              {step === 1 ? (
                  <div className="space-y-6">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-blue-600" /> Company Details
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                                <input 
                                    type="text"
                                    value={formData.companyName}
                                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g. NextGen Bio"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Development Stage</label>
                                <select
                                    value={formData.stage}
                                    onChange={(e) => setFormData({...formData, stage: e.target.value as CompanyStage})}
                                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {Object.values(CompanyStage).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => setStep(2)}
                        className="w-full flex items-center justify-center px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        Next Step <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                  </div>
              ) : (
                  <div className="space-y-6">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-blue-600" /> Your Focus
                        </h2>
                        
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-slate-700 mb-3">Therapeutic Areas of Interest</label>
                            <div className="flex flex-wrap gap-2">
                                {Object.values(TherapeuticArea).map(area => (
                                    <button 
                                        key={area}
                                        onClick={() => toggleSelection(area, formData.therapeuticAreas, 'therapeuticAreas')}
                                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${formData.therapeuticAreas.includes(area) ? 'bg-blue-100 border-blue-500 text-blue-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        {area}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-3">Target Markets</label>
                            <div className="flex flex-wrap gap-2">
                                {Object.values(Region).map(region => (
                                    <button 
                                        key={region}
                                        onClick={() => toggleSelection(region, formData.regionsOfInterest, 'regionsOfInterest')}
                                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${formData.regionsOfInterest.includes(region) ? 'bg-purple-100 border-purple-500 text-purple-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        {region.split('(')[0].trim()}
                                    </button>
                                ))}
                            </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <button 
                            onClick={() => setStep(1)}
                            className="w-1/3 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                            disabled={isSaving}
                        >
                            Back
                        </button>
                        <button 
                            onClick={handleFinish}
                            disabled={isSaving}
                            className="w-2/3 flex items-center justify-center px-8 py-3 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors disabled:opacity-70"
                        >
                            {isSaving ? 'Saving Profile...' : 'Explore Directory'}
                        </button>
                      </div>
                  </div>
              )}
          </div>
       </div>
    </div>
  );
};
